/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import { PrimaryButton, FieldWrapper, LightInput, GeneralFormError } from 'ui';
import { LoginSchema } from 'utils/validation';
import { FluidPreloader } from 'components/core/preloader';
import { actions } from 'data';
import { sitesPath } from 'constants/urls';
import s from './styles.module.scss';

interface WeChatLoginLinkFormData {
  username: string;
  password: string;
}

const WeChatLoginLink: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registrationToken = localStorage.getItem('wechat_registration_token');

  const validationSchema = LoginSchema(t);

  const handleSubmit = async (values: WeChatLoginLinkFormData) => {
    if (!registrationToken) {
      setError(t('wechat_login_link_no_token'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await dispatch(
        actions.wechat.wechatLoginLink({
          registration_token: registrationToken,
          username: values.username,
          password: values.password,
        }) as any,
      );

      console.log('WeChat login link response:', result);

      // Clear the stored token
      localStorage.removeItem('wechat_registration_token');

      // Redirect to main application
      navigate(sitesPath, { replace: true });
    } catch (error) {
      console.error('WeChat login link error:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    // Clear stored data
    // localStorage.removeItem('wechat_registration_token');
    navigate('/', { replace: true });
  };

  if (isLoading) {
    return (
      <div className={s.sharedForm}>
        <div className={s.wechatCallbackContainer}>
          <h2 className={s.wechatCallbackTitle}>{t('wechat_login_link_processing')}</h2>
          <FluidPreloader />
          <p className={s.wechatCallbackProcessingText}>{t('wechat_login_link_please_wait')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={s.sharedForm}>
      <div className={s.wechatCallbackContainer}>
        <h2 className={s.wechatCallbackTitle}>{t('wechat_login_link_title')}</h2>
        <p className={s.wechatCallbackProcessingText}>{t('wechat_login_link_description')}</p>

        <Formik
          initialValues={{
            username: '',
            password: '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, handleChange, handleBlur, handleSubmit }) => (
            <Form>
              <GeneralFormError error={error} />

              <Field
                id="login"
                name="username"
                placeholder={t('email')}
                component={FieldWrapper}
                inputComponent={LightInput}
                className={s.sharedFormField}
                error={errors.username && touched.username ? errors.username : null}
              />

              <Field
                id="password"
                name="password"
                type="password"
                placeholder={t('password')}
                component={FieldWrapper}
                inputComponent={LightInput}
                className={s.sharedFormField}
                error={errors.password && touched.password ? errors.password : null}
              />

              <PrimaryButton type="submit" className={s.sharedFormSubmit} onClick={handleSubmit}>
                {t('link_wechat_account')}
              </PrimaryButton>
            </Form>
          )}
        </Formik>

        <div className={s.wechatLoginLinkBack}>
          <a
            href="#"
            className={s.sharedFormFooterLink}
            onClick={(e) => {
              e.preventDefault();
              handleBackToLogin();
            }}
          >
            {t('back_to_login')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default WeChatLoginLink;
